/**
 * ROAR-4945 (ROAR-4899 AC2) — the single source of truth for the DIRECTION of a usage-ledger row.
 *
 * `GET /billing/credits/ledger` sends `credits_used` as `Math.abs(credit_ledger.amount)` and
 * carries the sign in `entry_type` instead. The pane rendered the magnitude alone, so a refund of
 * 50 credits was pixel-identical to a spend of 50 credits — on a billing screen, the one place
 * that distinction is the whole point of the table.
 *
 * A registry rather than a branch at the call site, per CLAUDE.md: the backend's `creditEntryTypes`
 * can gain a member, and an unregistered one must render UNSIGNED rather than silently inheriting
 * whichever branch happened to be the `else`.
 *
 * `reserve` and `release` are on the backend's deny-list and never reach this table, so they are
 * deliberately absent: if one ever arrives it resolves to `unknown` and renders unsigned, rather
 * than being mislabelled as a spend.
 */
export const CREDIT_ENTRY_DIRECTIONS = Object.freeze({
  // Credits leaving the balance.
  consume: 'debit',
  expire: 'debit',
  // Credits arriving.
  grant: 'credit',
  refund: 'credit',
  /**
   * Signed in the DATABASE, but unrecoverable here: the wire sends only `Math.abs(amount)`, so an
   * adjustment's direction genuinely cannot be known client-side. Mapped explicitly rather than
   * left to fall through, so this is a recorded decision rather than an oversight — and so that
   * closing the gap means changing the CONTRACT (returning a signed value or a direction), not
   * guessing harder here.
   */
  adjustment: 'unknown',
});

/** What an empty cell shows, matching the table's other "no value" cells. */
const NO_VALUE = '-';

/**
 * The direction of a ledger entry.
 *
 * @param {string} [entryType] `credit_ledger.entry_type` as sent on the wire.
 * @returns {'debit'|'credit'|'unknown'}
 */
export const resolveEntryDirection = (entryType) => {
  const key = String(entryType ?? '');
  // OWN-property only: `CREDIT_ENTRY_DIRECTIONS['constructor']` would otherwise resolve an
  // inherited function and be treated as a direction.
  return Object.prototype.hasOwnProperty.call(CREDIT_ENTRY_DIRECTIONS, key)
    ? CREDIT_ENTRY_DIRECTIONS[key]
    : 'unknown';
};

/**
 * The CREDITS USED cell: the magnitude, prefixed with its direction.
 *
 * A numeric STRING is accepted on purpose — Postgres `NUMERIC`/`BIGINT` columns serialize as
 * strings through Sequelize, so `'12'` is a legitimate wire value and dropping it to the dash would
 * blank a real row.
 *
 * Zero is never signed: `-0` reads as a loss that did not happen.
 *
 * @param {object} [row] A ledger row (`entry_type`, `credits_used`).
 * @returns {string} The formatted cell, or `'-'` when there is no amount.
 */
export const formatLedgerCredits = (row) => {
  const raw = row?.credits_used;
  // Only a number or a numeric string is an amount. Without the typeof guard `Number([])` and
  // `Number(true)` coerce to 0 and 1, so a malformed payload would render a confident, wrong
  // figure on a billing table instead of admitting it has nothing to show.
  if (typeof raw !== 'number' && typeof raw !== 'string') return NO_VALUE;
  if (String(raw).trim() === '') return NO_VALUE;

  const amount = Number(raw);
  if (!Number.isFinite(amount)) return NO_VALUE;

  const magnitude = Math.abs(amount);
  if (magnitude === 0) return '0';

  const direction = resolveEntryDirection(row?.entry_type);
  if (direction === 'debit') return `-${magnitude}`;
  if (direction === 'credit') return `+${magnitude}`;
  return `${magnitude}`;
};
